import { t } from "@comact/crc";
import React from "react";
import * as ReactDOM from "react-dom";
import { computeCoachPosition, findPill, ICoachPosition } from "./coachMark";
import "./switchUi.css";

const TITLE_ID = "switchUiCoachTitle";
const TEXT_ID = "switchUiCoachText";

interface ICoachMarkProps {

    /** The "Try the new version" button: the caller records that the coach-mark was seen, then switches. */
    onTry: () => void;

    /** "Not now", Esc or a click outside: the caller records that the coach-mark was seen. */
    onDismiss: () => void;
}

/**
 * The one-time introduction to the pill in the header: a small non-modal popover, drawn in `document.body` (the crc header has no slot for it),
 * anchored under the pill and measured from the pill's rectangle. The page behind stays usable; Esc, "Not now" and a click anywhere else dismiss it.
 * The glow ring on the pill is not drawn here: SwitchUiHeaderItem sets it through a data attribute on `<html>` (switchUi.css).
 */
const CoachMark = ({ onTry, onDismiss }: ICoachMarkProps) => {
    const popoverRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<ICoachPosition | null>(null);

    const update = React.useCallback(() => {
        const pill = findPill();
        const popover = popoverRef.current;
        if (!pill || !popover) {
            setPosition(null); // the pill is gone (header redrawn, sign-out): hide rather than float in the wrong place
            return;
        }
        const next = computeCoachPosition(pill.getBoundingClientRect(), popover.offsetWidth, document.documentElement.clientWidth);
        setPosition((previous) => (previous && previous.left === next.left && previous.top === next.top && previous.arrow === next.arrow ? previous : next));
    }, []);

    React.useLayoutEffect(() => {
        update();
    }, [update]);

    // Follow the pill: window resize (the label changes below 1100px), scrolling of any ancestor, and the pill changing size.
    React.useEffect(() => {
        let frame = 0;

        const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        window.addEventListener("resize", schedule);
        window.addEventListener("scroll", schedule, true);
        let observer: ResizeObserver | undefined;
        const pill = findPill();
        if (pill && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(schedule);
            observer.observe(pill);
        }
        const settle = window.setTimeout(schedule, 400); // the header may still be laying out (fonts, other items)
        return () => {
            cancelAnimationFrame(frame);
            window.clearTimeout(settle);
            window.removeEventListener("resize", schedule);
            window.removeEventListener("scroll", schedule, true);
            observer?.disconnect();
        };
    }, [update]);

    React.useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onDismiss();
        };

        const onPointerDown = (e: PointerEvent) => {
            if (popoverRef.current?.contains(e.target as Node)) return;
            onDismiss();
        };
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [onDismiss]);

    return ReactDOM.createPortal(
        <div
            ref={popoverRef}
            className="switchUiCoach"
            role="dialog"
            aria-modal="false"
            aria-labelledby={TITLE_ID}
            aria-describedby={TEXT_ID}
            style={position ? { left: position.left, top: position.top } : { left: 0, top: 0, visibility: "hidden" }}
        >
            <span className="switchUiCoach-arrow" aria-hidden="true" style={{ left: position?.arrow ?? 40 }} />
            <div className="switchUiCoach-eyebrow">{t("uiSwitch.coach.eyebrow")}</div>
            <h3 id={TITLE_ID} className="switchUiCoach-title">{t("uiSwitch.coach.title")}</h3>
            <p id={TEXT_ID} className="switchUiCoach-text">{t("uiSwitch.coach.text")}</p>
            <div className="switchUiCoach-actions">
                <button type="button" className="switchUiCoach-btn switchUiCoach-btnPrimary" onClick={onTry}>{t("uiSwitch.tryNew")}</button>
                <button type="button" className="switchUiCoach-btn switchUiCoach-btnGhost" onClick={onDismiss}>{t("uiSwitch.coach.later")}</button>
            </div>
        </div>,
        document.body
    );
};

export default CoachMark;
